// Texts of the screens taken over 1:1 from the prototype canvas "Brewcore neue Oberfläche" (Dominik, 21.10.2026:
// "Bitte 1:1 übernehmen von unserem Prototypen"): the wording of its boards, English alongside.
import type { Lang } from './i18n.js';

export const PROTO_TEXTS = {
  // charts in lanes
  usualBand: { de: 'üblich', en: 'usual' },
  avgShort: { de: 'Ø', en: 'avg' },
  wholeShot: { de: 'Ganzer Bezug', en: 'Whole shot' },
  atSeconds: { de: 'Bei {s} s', en: 'At {s} s' },
  scrubHint: {
    de: 'Zum Ablesen über die Kurve streichen.',
    en: 'Slide across the curve to read the values.',
  },
  shotChartLabel: { de: 'Kurve des Bezugs: {v}', en: 'Shot curve: {v}' },
  liveChartLabel: { de: 'Bezug läuft seit {s} Sekunden, {v}', en: 'Brewing for {s} seconds, {v}' },
  legendPower: { de: 'Heizung (%)', en: 'Heater (%)' },
  legendTime: { de: 'Zeit (s)', en: 'Time (s)' },
  minutesAgo: { de: 'vor {n} min', en: '{n} min ago' },
  secondsAgo: { de: 'vor {n} s', en: '{n} s ago' },
  nowShort: { de: 'jetzt', en: 'now' },
  historyChartLabel: {
    de: 'Temperatur der letzten 20 Minuten, zuletzt {v}',
    en: 'Temperature over the last 20 minutes, now {v}',
  },
  barsLabel: {
    de: 'Zeit und Gewicht der letzten {n} Bezüge. {off} davon neben dem Ziel.',
    en: 'Time and weight of the last {n} shots. {off} of them off target.',
  },
  barsTitle: { de: 'Die letzten {n}', en: 'The last {n}' },
  barsInfo: {
    de: 'Der neueste Bezug steht rechts. Rote Säulen lagen über 1 g oder 1 s neben dem Ziel. Für die Werte eine Säule antippen.',
    en: 'The newest shot is on the right. Red columns were over 1 g or 1 s off target. Tap a column to see its values.',
  },

  // machine: the line under the temperature
  readyIn: { de: 'bereit in ~{n} min', en: 'ready in ~{n} min' },
  readyInCap: { de: 'Bereit in ~{n} min', en: 'Ready in ~{n} min' },
  standbyAt: { de: 'Standby um {t}', en: 'Standby at {t}' },
  // the schedule's ready time: its switch-on plus the learned heat-up (Dominik 21.10.2026, "Bereit um")
  readyAtTime: { de: 'Bereit um {t}', en: 'Ready at {t}' },
  readyDayAt: { de: 'Bereit {d} um {t}', en: 'Ready {d} at {t}' },
  pausedUntil: { de: 'Pausiert bis {d}', en: 'Paused until {d}' },
  heaterOff: { de: 'Heizung aus', en: 'Heater off' },
  switchOn: { de: 'Einschalten', en: 'Switch on' },
  shotsLabel: { de: 'Bezüge', en: 'Shots' },
  scheduleRow: { de: 'Zeitplan', en: 'Schedule' },
  scheduleOff: { de: 'aus', en: 'off' },
  historyLink: { de: 'Verlauf', en: 'History' },

  // the last shot against its usual range: the bean's last good shots with its recipe (usualShots)
  asUsual: { de: 'wie üblich', en: 'as usual' },
  usualFaster: { de: '{n} s schneller als üblich', en: '{n} s faster than usual' },
  usualSlower: { de: '{n} s langsamer als üblich', en: '{n} s slower than usual' },
  usualMore: { de: '{n} g mehr als üblich', en: '{n} g more than usual' },
  usualLess: { de: '{n} g weniger als üblich', en: '{n} g less than usual' },
  usualNoteOk: {
    de: 'Zeit und Gewicht wie die letzten {k} guten.',
    en: 'In line with {k} recent good shots.',
  },
  usualNoteLonger: {
    de: '{n} s länger als die letzten {k} guten.',
    en: '{n} s longer than {k} recent good shots.',
  },
  usualNoteShorter: {
    de: '{n} s kürzer als die letzten {k} guten.',
    en: '{n} s shorter than {k} recent good shots.',
  },
  usualNoteMore: {
    de: '{n} g mehr als die letzten {k} guten.',
    en: '{n} g more than {k} recent good shots.',
  },
  usualNoteLess: {
    de: '{n} g weniger als die letzten {k} guten.',
    en: '{n} g less than {k} recent good shots.',
  },
  n2: { de: 'zwei', en: 'two' },
  n3: { de: 'drei', en: 'three' },
  n4: { de: 'vier', en: 'four' },
  n5: { de: 'fünf', en: 'five' },

  // the running shot and "Fertig"
  brewTempLive: { de: 'Brühtemperatur', en: 'Brew temperature' },
  ofGrams: { de: 'von {n} g', en: 'of {n} g' },
  ofSeconds: { de: 'von {n} s', en: 'of {n} s' },
  phaseBrew: { de: 'Bezug', en: 'Brewing' },
  phaseDone: { de: 'Fertig', en: 'Done' },
  tipSour: {
    de: 'Zu sauer. Bitte feiner mahlen, {to} statt {from}.',
    en: 'Too sour. Please grind finer, {to} instead of {from}.',
  },
  tipBitter: {
    de: 'Zu bitter. Bitte gröber mahlen, {to} statt {from}.',
    en: 'Too bitter. Please grind coarser, {to} instead of {from}.',
  },
  tipSourPlain: {
    de: 'Zu sauer. Bitte feiner mahlen.',
    en: 'Too sour. Please grind finer.',
  },
  tipBitterPlain: {
    de: 'Zu bitter. Bitte gröber mahlen.',
    en: 'Too bitter. Please grind coarser.',
  },
  tipGood: { de: 'Gut. Das Rezept bleibt, wie es ist.', en: 'Good. The recipe stays as it is.' },
  // one line on the narrowest phone (23.10.2026: "Mahlgrad 2,3 übernehmen" wrapped at 360 px); the text above names it
  tipApply: { de: '{to} übernehmen', en: 'Use {to}' },
  tipDone: { de: 'Mahlgrad {to} übernommen.', en: 'Grind size set to {to}.' },
  curveRow: { de: 'Kurve und Kennwerte', en: 'Curve and details' },
  curveRowHint: { de: 'Stichwörter und Notiz hinzufügen', en: 'Add tags and a note' },

  bagLeft: { de: '~{g} g', en: '~{g} g' },

  // care (prototype "Wartung")
  aboutTomorrow: { de: 'etwa morgen', en: 'about tomorrow' },
  aboutWeekday: { de: 'etwa am {d}', en: 'about {d}' },
  earlyMonth: { de: 'Anfang {m}', en: 'early {m}' },
  midMonth: { de: 'Mitte {m}', en: 'mid {m}' },
  lateMonth: { de: 'Ende {m}', en: 'late {m}' },
  dripAlmostFull: { de: 'fast voll, {ml} von {cap} ml', en: 'almost full, {ml} of {cap} ml' },
  dripEmpty: { de: 'leer', en: 'empty' },
  bfDueNow: { de: 'fällig, {n} Bezüge seit dem letzten', en: 'due, {n} shots since the last one' },
  bfDueIn: { de: 'in {n} Bezügen, {when}', en: 'in {n} shots, {when}' },
  bfDueInN: { de: 'in {n} Bezügen', en: 'in {n} shots' },
  dsDueNow: { de: 'fällig, {l} l seit dem letzten Mal', en: 'due, {l} l since the last time' },
  dsDueIn: { de: 'in ~{l} l, {when}', en: 'in ~{l} l, {when}' },
  dsDueInL: { de: 'in ~{l} l', en: 'in ~{l} l' },
  sinceDescale: { de: 'seit Entkalken', en: 'since descaling' },
  flushRowHint: { de: '3 kurze Stöße zum Vorwärmen', en: '3 short bursts to warm up' },
  descaleRowHint: { de: 'Dauert ~1 Stunde', en: 'Takes ~1 hour' },
  bfWithout: { de: 'Ohne Reiniger', en: 'Without cleaner' },
  bfWith: { de: 'Mit Reiniger', en: 'With cleaner' },
  careSettingsRow: { de: 'Erinnerungen', en: 'Reminders' },
  careRemindBf: { de: 'Backflush alle {n} Bezüge', en: 'Backflush every {n} shots' },
  careRemindDs: { de: 'Entkalken alle {l} l', en: 'Descale every {l} l' },

  // settings (prototype "Reiter Einstellungen")
  standbyAfter: { de: 'Nach', en: 'After' },
  keepWarm: { de: 'Warm halten', en: 'Keep warm' },
  schedWorkdays: { de: 'Werktags bereit um', en: 'Ready on workdays at' },
  schedWeekendAt: { de: 'Am Wochenende um {t}.', en: 'At the weekend at {t}.' },
  schedWeekendNone: { de: 'Am Wochenende aus.', en: 'Off at the weekend.' },
  schedNextLabel: { de: 'Als Nächstes bereit um', en: 'Next ready at' },
  preheatToday: { de: 'Heute beginnt das Vorheizen um {t}.', en: 'Preheating starts today at {t}.' },
  preheatTomorrow: { de: 'Morgen beginnt das Vorheizen um {t}.', en: 'Preheating starts tomorrow at {t}.' },
  preheatInfo: {
    de: 'Das Vorheizen dauert ~{n} min, gemessen an den letzten {k} Kaltstarts. Bitte Hauptschalter eingeschaltet lassen.',
    en: 'Preheating takes ~{n} min, based on the last {k} cold starts. Please leave the main switch on.',
  },
  preheatInfoOne: {
    de: 'Das Vorheizen dauert ~{n} min, gemessen am letzten Kaltstart. Bitte Hauptschalter eingeschaltet lassen.',
    en: 'Preheating takes ~{n} min, based on the last cold start. Please leave the main switch on.',
  },
  preheatInfoNone: {
    de: 'Das Vorheizen dauert ~{n} min. Die App misst es bei den nächsten Kaltstarts genauer. Bitte Hauptschalter eingeschaltet lassen.',
    en: 'Preheating takes ~{n} min. The app refines this over the next cold starts. Please leave the main switch on.',
  },
  scheduleOn: { de: 'Zeitplan an', en: 'Schedule on' },
  scheduleOffText: {
    de: 'Zeitplan ist aus. Bitte oben einschalten.',
    en: 'Schedule is off. Please turn it on above.',
  },
  holidayRowHint: { de: 'Zeitplan bis zu einem Datum pausieren', en: 'Pause the schedule until a date' },
  standbyRowHint: { de: 'Nach {n} min ohne Bezug', en: 'After {n} min without a shot' },
  planMax: { de: 'Höchstens {n} pro Tag', en: 'At most {n} a day' },

  // the recipe (prototype "Rezept")
  changedFrom: { de: 'Geändert gegenüber {p}', en: 'Changed from {p}' },
  roastedOn: { de: 'geröstet am {d}', en: 'roasted {d}' },
  saveAsPreset: { de: 'Als {p} speichern', en: 'Save as {p}' },
  resetRecipe: { de: 'Zurücksetzen', en: 'Reset' },
  amount: { de: 'Menge', en: 'Amount' },
  amountInfo: {
    de: 'Das Zielgewicht ergibt sich aus Dosis und Verhältnis.',
    en: 'The target weight follows from dose and ratio.',
  },
  stopsAt: { de: 'Stoppt bei', en: 'Stops at' },
  stopsAfter: { de: 'Stoppt nach', en: 'Stops after' },
  ratioLess: { de: 'Verhältnis kleiner', en: 'Smaller ratio' },
  ratioMore: { de: 'Verhältnis größer', en: 'Larger ratio' },
  piHint: { de: '{b} s Stoß, dann {p} s Pause', en: '{b} s burst, then {p} s pause' },
  fineHint: { de: 'Temperaturverlauf, Nachlaufzeit', en: 'Temperature curve, stop lag' },
  done: { de: 'Fertig', en: 'Done' },

  // the presets (prototype "Schnellwahl")
  lastUsed: { de: 'Zuletzt genutzt', en: 'Used last' },
  presetsIntro: {
    de: 'Eine Schnellwahl speichert ein ganzes Rezept. Sie steht oben im Reiter „Bezug“ und im Rezept.',
    en: 'A preset saves a whole recipe. They’re at the top of the Brew tab and in the recipe.',
  },
  saveCurrent: { de: 'Aktuelles Rezept speichern', en: 'Save current recipe' },
  saveCurrentTitle: { de: 'In welche Schnellwahl?', en: 'Into which preset?' },
  saveInto: { de: 'Als {p} speichern', en: 'Save as {p}' },

  // a shot's page (prototype "Ein Bezug")
  phases: { de: 'Phasen', en: 'Phases' },
  phaseStart: { de: 'Bis zum ersten Tropfen', en: 'Until the first drop' },
  phaseToDrop: { de: 'bis zum ersten Tropfen', en: 'until the first drop' },
  phaseFlowSteady: { de: 'gleichmäßig mit Ø {f} g/s', en: 'avg {f} g/s, steady' },
  phaseFlowAvg: { de: 'mit Ø {f} g/s', en: 'avg {f} g/s' },
  phaseFlowUneven: { de: 'unruhig, Ø {f} g/s', en: 'uneven, avg {f} g/s' },
  phaseDripHint: { de: 'nachdem die Pumpe bei {g} g stoppte', en: 'after the pump stopped at {g} g' },
  shotSettingsTitle: { de: 'Einstellungen dieses Bezugs', en: 'Settings for this shot' },
  prevShot: { de: 'Voriger Bezug', en: 'Previous shot' },
  compareChartLabel: { de: 'Kurven im Vergleich: {v}', en: 'Curves compared: {v}' },
  nextShot: { de: 'Nächster Bezug', en: 'Next shot' },
  refRemember: { de: 'Als Referenz merken', en: 'Use as reference' },

  // the history (prototype "Verlauf")
  nOfM: { de: '{n} von {m}', en: '{n} of {m}' },
  shotOne: { de: 'Bezug', en: 'shot' },
  shotsWord: { de: 'Bezüge', en: 'shots' },
  beforeToday: { de: 'gestern', en: 'yesterday' },
  beforeWeek: { de: 'in der Woche davor', en: 'the week before' },
  before30: { de: 'in den 30 Tagen davor', en: 'the 30 days before' },
  beforeMonth: { de: 'im {month}', en: 'in {month}' },
  moreThanBefore: { de: '{n} mehr als {when}', en: '{n} more than {when}' },
  lessThanBefore: { de: '{n} weniger als {when}', en: '{n} fewer than {when}' },
  sameAsBefore: { de: 'so viele wie {when}', en: 'as many as {when}' },
  verdictAll: { de: 'Alle Bezüge liegen im üblichen Bereich.', en: 'All shots are within the usual range.' },
  verdictMostOne: {
    de: 'Die meisten Bezüge liegen im üblichen Bereich. Einer lag daneben.',
    en: 'Most shots are within the usual range. One was off.',
  },
  verdictMost: {
    de: 'Die meisten Bezüge liegen im üblichen Bereich. {n} lagen daneben.',
    en: 'Most shots are within the usual range. {n} were off.',
  },
  verdictMany: {
    de: '{n} von {m} Bezügen lagen neben dem Ziel.',
    en: '{n} of {m} shots missed their target.',
  },
  w2: { de: 'Zwei', en: 'Two' },
  w3: { de: 'Drei', en: 'Three' },
  w4: { de: 'Vier', en: 'Four' },
  w5: { de: 'Fünf', en: 'Five' },
  viewLabel: { de: 'Ansicht', en: 'View' },
  viewList: { de: 'Liste', en: 'List' },
  viewCalendar: { de: 'Kalender', en: 'Calendar' },
  dotHint: { de: 'Jeder Punkt ist ein Bezug.', en: 'Each dot is one shot.' },
  calDay: { de: '{d}.: {n} Bezüge', en: '{d}: {n} shots' },
  calNone: { de: 'Keine Bezüge an diesem Tag.', en: 'No shots on this day.' },
} as const satisfies Record<string, Record<Lang, string>>;
